import { Spinner, useTranslation } from '@hbs-us/ui';
import { getRouteApi } from '@tanstack/react-router';

import { EmployeeDetailView } from './EmployeeDetailView.js';
import { useEmployeeQuery } from './useEmployeeQuery.js';

/**
 * The `/employees/$id` route component (promoted from a ComingSoon stub this plan).
 * Reads the `id` path param, fires the coarse detail query, and renders the
 * redesigned `EmployeeDetailView` on success. Loading → accessible `Spinner`; error
 * (or unknown/cross-tenant id → 404) → announced error state with a retry (the
 * reports/invoices/estimates 3-state contract, Shared 9).
 */

const routeApi = getRouteApi('/employees/$id');

export function EmployeeDetailRoute() {
  const { t } = useTranslation();
  const { id } = routeApi.useParams();
  const employeeId = Number(id);
  const query = useEmployeeQuery(employeeId);

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (query.isError || !query.data.employee) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('employees:error.detailHeading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('employees:error.detailBody')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('employees:error.retry')}
        </button>
      </section>
    );
  }

  return <EmployeeDetailView employee={query.data.employee} />;
}
